/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowPathIcon,
  ArchiveBoxArrowDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SignalIcon,
  EllipsisVerticalIcon,
  MagnifyingGlassIcon,
  WrenchScrewdriverIcon,
  PencilSquareIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/Button";
import { useFocus, useReportSelected } from "@/utils/sidebarFocus";
import { useMobileHeaderAction } from "@/context/tabNavigation";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState, ErrorState, SkeletonRows } from "@/components/common/States";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { showToast } from "@/components/common/Toast";
import {
  backupRouter,
  checkRouterDNS,
  fixRouterYouTube,
  getRouter,
  getRouterAgents,
  getRouterCheckStreamUrl,
  getRouters,
  installRouterDNSWatchdog,
  restartRouterService,
  updateRouterProfile,
  withAgent,
  RouterCheckEvent,
  RouterProfile,
  RouterStatus,
} from "@/api/routers";
import { ConsoleButton } from "@/components/terminal/ConsoleButton";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/utils/language";
import { Bar, Meter, StatusDot, fmtDuration } from "@/components/servers/kit";

const formatBytes = (bytes?: number) => {
  if (!bytes || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const formatKb = (kb?: number) => formatBytes((kb || 0) * 1024);

const ROUTER_RAM_WARNING_PERCENT = 85;
const ROUTER_RAM_CRITICAL_PERCENT = 92;
const ROUTER_RAM_CRITICAL_AVAILABLE_KB = 4 * 1024;

const getRouterRamHealth = (stats?: RouterStatus["memoryStats"]): "normal" | "warning" | "critical" => {
  if (!stats) return "normal";
  if (
    stats.usedPercent >= ROUTER_RAM_CRITICAL_PERCENT ||
    stats.availableKb > 0 && stats.availableKb < ROUTER_RAM_CRITICAL_AVAILABLE_KB
  ) {
    return "critical";
  }
  if (stats.usedPercent >= ROUTER_RAM_WARNING_PERCENT) {
    return "warning";
  }
  return "normal";
};

const CheckStateIcon: React.FC<{ status: RouterCheckEvent["status"] }> = ({ status }) => {
  if (status === "running") {
    return <ArrowPathIcon className="h-4 w-4 animate-spin text-blue-500" />;
  }
  if (status === "ok") {
    return <CheckCircleIcon className="h-4 w-4 text-emerald-500" />;
  }
  if (status === "error") {
    return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
  }
  return <CheckCircleIcon className="h-4 w-4 text-gray-400" />;
};

const RouterCheckModal: React.FC<{
  router: RouterStatus | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ router, open, onOpenChange }) => {
  const { t } = useLanguage();
  const [events, setEvents] = useState<RouterCheckEvent[]>([]);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!open || !router) return;

    setEvents([]);
    setStreamError(null);
    setRunning(true);

    const source = new EventSource(getRouterCheckStreamUrl(router.id));
    source.onmessage = (message) => {
      const event = JSON.parse(message.data) as RouterCheckEvent;
      setEvents((prev) => {
        const index = prev.findIndex(
          (item) => item.group === event.group && item.name === event.name && item.target === event.target,
        );
        if (index >= 0) {
          const next = [...prev];
          next[index] = event;
          return next;
        }
        return [...prev, event];
      });
      if (event.group === "done") {
        setRunning(false);
        source.close();
      }
    };
    source.onerror = () => {
      setRunning(false);
      setStreamError(t("checkConnectionLost"));
      source.close();
    };

    return () => {
      source.close();
      setRunning(false);
    };
  }, [open, router, t]);

  const groups = useMemo(
    () => ({
      ssh: events.filter((event) => event.group === "ssh"),
      services: events.filter((event) => event.group === "services"),
      youtube: events.filter((event) => event.group === "youtube"),
      done: events.find((event) => event.group === "done"),
    }),
    [events],
  );

  const sectionStatus = (items: RouterCheckEvent[]) => {
    if (items.some((item) => item.status === "running")) return "running";
    if (items.length === 0) return "done";
    return items.every((item) => item.ok) ? "ok" : "error";
  };

  const renderRows = (items: RouterCheckEvent[]) => (
    <div className="space-y-2">
      {items.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-500 dark:border-gray-800 dark:text-gray-400">
          {t("waitingForCheck")}
        </div>
      ) : (
        items.map((event) => (
          <div
            key={`${event.group}-${event.name}-${event.target || ""}`}
            className="rounded-md border border-gray-200 bg-white/70 px-3 py-2 dark:border-gray-800 dark:bg-gray-950/30"
          >
            <div className="flex items-center gap-2">
              <CheckStateIcon status={event.status} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-gray-800 dark:text-gray-200">
                  {event.name}
                </div>
                {event.detail && (
                  <div className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400" title={event.detail}>
                    {event.detail}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-1rem)] max-w-[calc(100%-1rem)] sm:max-w-2xl p-0 overflow-hidden">
        <DialogHeader className="border-b border-gray-200 p-4 text-left dark:border-gray-800">
          <DialogTitle>{t("availabilityCheck")}</DialogTitle>
          <DialogDescription>
            {router?.name || t("routerFallbackName")}: {t("routerCheckScope")}
          </DialogDescription>
        </DialogHeader>

        <div className="cc-scroll max-h-[70vh] space-y-4 overflow-y-auto p-4">
          <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-800">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
              <CheckStateIcon status={sectionStatus(groups.ssh)} />
              SSH
            </div>
            {renderRows(groups.ssh)}
          </div>

          <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-800">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
              <CheckStateIcon status={sectionStatus(groups.youtube)} />
              YouTube
            </div>
            {renderRows(groups.youtube)}
          </div>

          <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-800">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
              <CheckStateIcon status={sectionStatus(groups.services)} />
              {t("services")}
            </div>
            {renderRows(groups.services)}
          </div>

          {streamError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/20 dark:text-red-300">
              {streamError}
            </div>
          )}

          {groups.done && (
            <div className={cn(
              "rounded-md border px-3 py-2 text-sm",
              groups.done.ok
                ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/20 dark:text-emerald-300"
                : "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-300",
            )}>
              {groups.done.ok ? t("allChecksPassed") : t("someChecksFailed")}
            </div>
          )}

          {running && (
            <div className="text-center text-xs text-gray-500 dark:text-gray-400">
              {t("checkStillRunning")}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const RouterProfileModal: React.FC<{
  router: RouterStatus | null;
  open: boolean;
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (id: string, profile: RouterProfile) => void;
}> = ({ router, open, saving, onOpenChange, onSave }) => {
  const { t } = useLanguage();
  const [profile, setProfile] = useState<RouterProfile>({});

  useEffect(() => {
    if (!router || !open) return;
    setProfile({
      displayName: router.profile?.displayName || router.name,
      location: router.profile?.location || "",
      owner: router.profile?.owner || "",
      purpose: router.profile?.purpose || "",
      notes: router.profile?.notes || "",
    });
  }, [open, router]);

  if (!router) return null;

  const updateField = (key: keyof RouterProfile, value: string) => {
    setProfile((current) => ({ ...current, [key]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-1rem)] max-w-[calc(100%-1rem)] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("routerProfile")}</DialogTitle>
          <DialogDescription>{t("routerProfileHint")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">{t("displayName")}</span>
            <input
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-950"
              value={profile.displayName || ""}
              onChange={(event) => updateField("displayName", event.target.value)}
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">{t("location")}</span>
              <input
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-950"
                value={profile.location || ""}
                onChange={(event) => updateField("location", event.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">{t("owner")}</span>
              <input
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-950"
                value={profile.owner || ""}
                onChange={(event) => updateField("owner", event.target.value)}
              />
            </label>
          </div>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">{t("purpose")}</span>
            <input
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-950"
              value={profile.purpose || ""}
              onChange={(event) => updateField("purpose", event.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">{t("notes")}</span>
            <textarea
              className="min-h-24 w-full resize-none rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-950"
              value={profile.notes || ""}
              onChange={(event) => updateField("notes", event.target.value)}
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
            <Button isLoading={saving} onClick={() => onSave(router.id, profile)}>{t("save")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

type RouterState = "up" | "warn" | "down";

const routerState = (r: RouterStatus): RouterState => {
  if (!r.online) return "down";
  const ram = getRouterRamHealth(r.memoryStats);
  return r.youtubeOk && r.servicesOk && r.dnsAdblockOk && r.dnsInternetOk && ram !== "critical" ? "up" : "warn";
};

const routerChecks = (r: RouterStatus, t: (k: string) => string) => [
  { key: "ssh", short: "SSH", label: "SSH", ok: r.online },
  { key: "youtube", short: "YT", label: "YouTube", ok: r.youtubeOk },
  { key: "services", short: "SVC", label: "Services", ok: r.servicesOk },
  { key: "ads", short: "ADS", label: t("dnsAdblock"), ok: r.dnsAdblockOk },
  { key: "dns", short: "DNS", label: t("dnsInternet"), ok: r.dnsInternetOk },
];

const internetPing = (r: RouterStatus) => {
  const hit = r.latency?.find((l) => l.name === "internet") ?? r.latency?.find((l) => l.ok);
  return hit && hit.ok && hit.timeMs ? `${Math.round(parseFloat(hit.timeMs))} ms` : "—";
};

const MiniMeter: React.FC<{ pct?: number; warn?: number; crit?: number }> = ({ pct, warn = 75, crit = 90 }) =>
  pct === undefined ? (
    <span className="rt-meter">
      <i className="sv-card-bar-off" />
      <b>—</b>
    </span>
  ) : (
    <span className="rt-meter">
      <Bar pct={pct} warn={warn} crit={crit} />
      <b>{Math.round(pct)}%</b>
    </span>
  );

const CheckTags: React.FC<{ router: RouterStatus }> = ({ router }) => {
  const { t } = useLanguage();
  const checks = routerChecks(router, t);
  const bad = checks.filter((c) => !c.ok);
  if (!router.online) {
    return <span className="rt-tag rt-tag--bad">SSH</span>;
  }
  if (bad.length === 0) {
    return (
      <span className="rt-allok">
        <CheckCircleIcon />
        {`All ${checks.length} checks pass`}
      </span>
    );
  }
  return (
    <>
      {bad.map((c) => (
        <span key={c.key} className="rt-tag rt-tag--bad" title={c.label}>
          {c.short}
        </span>
      ))}
    </>
  );
};

interface RouterActions {
  fixing: boolean;
  busyAction: boolean;
  onFix: (id: string) => void;
  onCheck: (router: RouterStatus) => void;
  onBackup: (id: string) => void;
  onCheckDNS: (id: string) => void;
  onInstallDNSWatchdog: (id: string) => void;
  onRestartService: (id: string, service: "zapret" | "dns" | "ssh-tunnel") => void;
  onEditProfile: (router: RouterStatus) => void;
}

const RouterMenu: React.FC<{ router: RouterStatus } & RouterActions> = ({
  router,
  fixing,
  busyAction,
  onFix,
  onCheck,
  onBackup,
  onCheckDNS,
  onInstallDNSWatchdog,
  onRestartService,
  onEditProfile,
}) => {
  const { t } = useLanguage();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="rt-iconbtn"
          aria-label={t("routerActions")}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <EllipsisVerticalIcon />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={6} className="w-56" onClick={(event) => event.stopPropagation()}>
        <DropdownMenuItem onSelect={() => onEditProfile(router)}>
          <PencilSquareIcon className="h-4 w-4" />
          <span>{t("editProfile")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onCheck(router)}>
          <MagnifyingGlassIcon className="h-4 w-4" />
          <span>{t("checkAvailability")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem disabled={busyAction} onSelect={() => onCheckDNS(router.id)}>
          <MagnifyingGlassIcon className="h-4 w-4" />
          <span>{t("checkDNS")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem disabled={busyAction} onSelect={() => onBackup(router.id)}>
          <ArchiveBoxArrowDownIcon className="h-4 w-4" />
          <span>{t("backupRouter")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!router.online || fixing} onSelect={() => onFix(router.id)}>
          <WrenchScrewdriverIcon className={cn("h-4 w-4", fixing && "animate-spin")} />
          <span>{fixing ? t("running") : t("fixYouTube")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!router.online || busyAction} onSelect={() => onRestartService(router.id, "zapret")}>
          <ArrowPathIcon className="h-4 w-4" />
          <span>{t("restartZapret")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!router.online || busyAction} onSelect={() => onRestartService(router.id, "dns")}>
          <ArrowPathIcon className="h-4 w-4" />
          <span>{t("restartDNS")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!router.online || busyAction} onSelect={() => onRestartService(router.id, "ssh-tunnel")}>
          <ArrowPathIcon className="h-4 w-4" />
          <span>{t("restartTunnel")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!router.online || busyAction} onSelect={() => onInstallDNSWatchdog(router.id)}>
          <WrenchScrewdriverIcon className="h-4 w-4" />
          <span>{t("installDNSWatchdog")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const RouterRow: React.FC<{ router: RouterStatus; onOpen: () => void } & RouterActions> = ({ router, onOpen, ...actions }) => {
  const { t } = useLanguage();
  const state = routerState(router);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className={`rt-trow rt-row rt-row--${state}`}
    >
      <span className="rt-id">
        <StatusDot state={state} />
        <div>
          <b>{router.name}</b>
          <small>
            {router.sshUser}@{router.sshHost}:{router.sshPort}
          </small>
        </div>
      </span>
      <span className="rt-cell" title={router.openwrt || router.model}>
        {router.model || router.openwrt || "—"}
      </span>
      <span className="rt-cell">{router.uptimeSec ? fmtDuration(router.uptimeSec) : "—"}</span>
      <span className="rt-cell rt-cell--num rt-load" title={[router.load1, router.load5, router.load15].filter(Boolean).join(" / ")}>
        {router.online && router.load1 ? router.load1 : "—"}
      </span>
      <span className="rt-cell">
        <MiniMeter pct={router.memoryStats?.usedPercent} warn={ROUTER_RAM_WARNING_PERCENT} crit={ROUTER_RAM_CRITICAL_PERCENT} />
      </span>
      <span className="rt-cell">
        <MiniMeter pct={router.storageStats?.usePercent} warn={80} crit={92} />
      </span>
      <span className="rt-cell rt-cell--num">{router.online ? internetPing(router) : "—"}</span>
      <span className="rt-cell rt-checks">
        <CheckTags router={router} />
      </span>
      <span className="rt-cell rt-act">
        <RouterMenu router={router} {...actions} />
      </span>
      <span className="rt-mobile-met">
        <span className="rt-sub">
          RAM <MiniMeter pct={router.memoryStats?.usedPercent} warn={ROUTER_RAM_WARNING_PERCENT} crit={ROUTER_RAM_CRITICAL_PERCENT} />
        </span>
        <span className="rt-sub">
          {t("overlay")} <MiniMeter pct={router.storageStats?.usePercent} warn={80} crit={92} />
        </span>
      </span>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode; pct?: number; warn?: number; crit?: number }> = ({
  label,
  value,
  sub,
  pct,
  warn,
  crit,
}) => (
  <div className="rt-box rt-stat">
    <h3>{label}</h3>
    <div className="rt-stat-v">{value}</div>
    {pct !== undefined && <Bar pct={pct} warn={warn} crit={crit} />}
    {sub && <div className="rt-stat-s">{sub}</div>}
  </div>
);

/** Per-host results of the YouTube probe ("www.youtube.com OK HTTP/204 ip=…"). */
const parseYouTube = (line?: string) =>
  (line || "")
    .split(" / ")
    .map((part) => part.trim())
    .filter((part) => /^\S+ (OK|FAIL)\b/.test(part))
    .map((part) => ({ host: part.split(" ")[0], ok: part.split(" ")[1] === "OK", detail: part }));

const RouterDetail: React.FC<{ router: RouterStatus; onBack: () => void; isFetching: boolean; onRefresh: () => void } & RouterActions> = ({
  router,
  onBack,
  isFetching,
  onRefresh,
  ...actions
}) => {
  const { t } = useLanguage();
  const state = routerState(router);
  const ramHealth = getRouterRamHealth(router.memoryStats);
  const checks = routerChecks(router, t);
  const yt = parseYouTube(router.youtubeLine);
  const profile = router.profile;
  const hasProfile = profile?.location || profile?.owner || profile?.purpose || profile?.notes;
  const badge = state === "up" ? t("healthy") : state === "down" ? "Offline" : t("needsAttention");

  const detail: Record<string, string | undefined> = {
    [t("hostname")]: router.hostname,
    [t("model")]: router.model,
    [t("openwrt")]: router.openwrt,
    [t("kernel")]: router.kernel,
    [t("defaultRoute")]: router.defaultRoute,
    [t("activeDNS")]: router.dnsMode,
  };

  return (
    <div className="gh sv rt">
      <div className="rt-head">
        <Button variant="ghost" size="sm" className="-ml-2" onClick={onBack}>
          <ArrowLeftIcon className="h-4 w-4" />
          <span>{t("backToRouters")}</span>
        </Button>
      </div>
      <div className="rt-head">
        <div style={{ minWidth: 0 }}>
          <h2>{router.name}</h2>
          <p>
            {router.sshUser}@{router.sshHost}:{router.sshPort}
            {router.checkedAt ? ` · ${new Date(router.checkedAt).toLocaleTimeString()}` : ""}
          </p>
        </div>
        <span className={`rt-badge rt-badge--${state}`}>
          <StatusDot state={state} />
          {badge}
        </span>
        <div className="rt-head-act">
          <ConsoleButton kind="router" id={router.id} name={router.name} />
          <button className="gh-btn" onClick={() => actions.onCheck(router)}>
            <MagnifyingGlassIcon />
            {t("checkAvailability")}
          </button>
          <button className="gh-btn" onClick={onRefresh} disabled={isFetching}>
            <ArrowPathIcon className={cn(isFetching && "animate-spin")} />
            {t("refresh")}
          </button>
          <RouterMenu router={router} {...actions} />
        </div>
      </div>

      {router.error && <div className="rt-error">{router.error}</div>}

      <div className="rt-grid">
        <h4 className="rt-sec">Resources</h4>
        <div className="rt-c3">
          <Stat
            label={t("uptime")}
            value={router.uptimeSec ? fmtDuration(router.uptimeSec) : "—"}
            sub={router.load1 ? `${t("load")} ${[router.load1, router.load5, router.load15].filter(Boolean).join(" / ")}` : undefined}
          />
        </div>
        <div className="rt-c3">
          <Stat
            label="CPU"
            value={router.cpu ? `${Math.round(router.cpu.busyPercent)}%` : "—"}
            pct={router.cpu?.busyPercent}
            sub={router.cpu ? `user ${router.cpu.userPercent}% · system ${router.cpu.systemPercent}%` : undefined}
          />
        </div>
        <div className="rt-c3">
          <Stat
            label={t("ram")}
            value={router.memoryStats ? `${router.memoryStats.usedPercent}%` : "—"}
            pct={router.memoryStats?.usedPercent}
            warn={ROUTER_RAM_WARNING_PERCENT}
            crit={ROUTER_RAM_CRITICAL_PERCENT}
            sub={
              router.memoryStats
                ? ramHealth === "normal"
                  ? `${formatKb(router.memoryStats.availableKb)} ${t("available")} / ${formatKb(router.memoryStats.totalKb)}`
                  : ramHealth === "critical"
                    ? t("ramCritical")
                    : t("ramWarning")
                : undefined
            }
          />
        </div>
        <div className="rt-c3">
          <Stat
            label={t("overlay")}
            value={router.storageStats ? `${router.storageStats.usePercent}%` : "—"}
            pct={router.storageStats?.usePercent}
            sub={router.storageStats ? `${router.storageStats.available} ${t("freeOf")} ${router.storageStats.size}` : undefined}
          />
        </div>

        <h4 className="rt-sec">Health</h4>
        <div className="rt-box rt-c7">
          <h3>
            Checks
            <span>
              {checks.filter((c) => c.ok).length}/{checks.length}
            </span>
          </h3>
          <div className="rt-checklist">
            {checks.map((c) => (
              <div key={c.key} className={`rt-check rt-check--${c.ok ? "ok" : "bad"}`}>
                {c.ok ? <CheckCircleIcon /> : <ExclamationTriangleIcon />}
                <b>{c.label}</b>
                {c.key === "youtube" && yt.length > 0 ? (
                  <span className="rt-hosts">
                    {yt.map((h) => (
                      <span key={h.host} className={`rt-tag ${h.ok ? "rt-tag--ok" : "rt-tag--bad"}`} title={h.detail}>
                        {h.host}
                      </span>
                    ))}
                  </span>
                ) : (
                  <span>
                    {c.key === "ssh" && (router.online ? "connected" : router.error || "unreachable")}
                    {c.key === "services" && (router.servicesOk ? "all services running" : "a service is not running")}
                    {c.key === "ads" && (router.dnsAdblockOk ? "ad domains are blocked" : "ad blocking is not working")}
                    {c.key === "dns" && (router.dnsInternetOk ? "resolves public names" : "cannot resolve public names")}
                    {c.key === "youtube" && (router.youtubeOk ? "reachable" : "not reachable")}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="rt-box rt-c5">
          <h3>Network</h3>
          {router.latency && router.latency.length > 0 && (
            <div className="rt-lat">
              {router.latency.map((check) => (
                <div key={`${check.name}-${check.target}`} className={check.ok ? "" : "bad"} title={check.target}>
                  <span>{check.name}</span>
                  <b>{check.ok ? `${Math.round(parseFloat(check.timeMs || "0"))} ms` : t("noReply")}</b>
                </div>
              ))}
            </div>
          )}
          {router.conntrack && (
            <Meter
              label="Conntrack"
              value={`${router.conntrack.count} / ${router.conntrack.max}`}
              pct={router.conntrack.usePercent}
              sub={`${router.conntrack.usePercent}% · ${t("connections")}`}
            />
          )}
          <dl className="rt-kv">
            {Object.entries(detail)
              .filter(([, v]) => v)
              .map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt>{k}</dt>
                  <dd>{v}</dd>
                </React.Fragment>
              ))}
          </dl>
        </div>

        <h4 className="rt-sec">Interfaces &amp; Wi-Fi</h4>
        {router.interfaces && router.interfaces.length > 0 && (
          <div className="rt-box rt-c6">
            <h3>
              {t("interfaces")}
              <span>{router.interfaces.length}</span>
            </h3>
            <div className="rt-scroll">
              <table className="rt-mini">
                <thead>
                  <tr>
                    <th>{t("name")}</th>
                    <th className="r">RX</th>
                    <th className="r">TX</th>
                    <th className="r">Err / drop</th>
                  </tr>
                </thead>
                <tbody>
                  {router.interfaces.map((iface) => {
                    const bad = iface.rxErrors + iface.rxDrops + iface.txErrors + iface.txDrops;
                    return (
                      <tr key={iface.name}>
                        <td>{iface.name}</td>
                        <td className="r">{formatBytes(iface.rxBytes)}</td>
                        <td className="r">{formatBytes(iface.txBytes)}</td>
                        <td className={cn("r", bad > 0 && "sv-warn")}>{bad > 0 ? `${iface.rxErrors + iface.txErrors} / ${iface.rxDrops + iface.txDrops}` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {router.wifiRadios && router.wifiRadios.length > 0 && (
          <div className="rt-box rt-c6">
            <h3>
              {t("wifi")}
              <span>{router.wifiRadios.length}</span>
            </h3>
            <div className="rt-scroll">
              <table className="rt-mini">
                <thead>
                  <tr>
                    <th>SSID</th>
                    <th>Channel</th>
                    <th>Band</th>
                    <th>Width</th>
                    <th className="r">TX power</th>
                  </tr>
                </thead>
                <tbody>
                  {router.wifiRadios.map((radio) => (
                    <tr key={`${radio.phy}-${radio.interface}`}>
                      <td>{radio.ssid || radio.interface || `phy${radio.phy}`}</td>
                      <td>{radio.channel || "—"}</td>
                      <td>{radio.frequency || "—"}</td>
                      <td>{radio.width || "—"}</td>
                      <td className="r">{radio.txPower || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {router.wifiClients && router.wifiClients.length > 0 && (
          <div className="rt-box rt-c12">
            <h3>
              {t("wifiClients")}
              <span>{router.wifiClients.length}</span>
            </h3>
            <div className="rt-scroll">
              <table className="rt-mini">
                <thead>
                  <tr>
                    <th>MAC</th>
                    <th>{t("interfaces")}</th>
                    <th>Signal</th>
                    <th>RX</th>
                    <th>TX</th>
                    <th>{t("expected")}</th>
                    <th className="r">{t("inactive")}</th>
                  </tr>
                </thead>
                <tbody>
                  {router.wifiClients.map((client) => {
                    const dbm = client.signalDbm;
                    const strength = dbm ? Math.max(0, Math.min(100, ((dbm + 95) / 50) * 100)) : 0;
                    const tone = !dbm ? "" : dbm >= -65 ? "" : dbm >= -75 ? "rt-sig--mid" : "rt-sig--low";
                    return (
                      <tr key={`${client.interface}-${client.mac}`}>
                        <td className="mono">{client.mac}</td>
                        <td>{client.interface}</td>
                        <td>
                          <span className={`rt-sig ${tone}`}>
                            <i>
                              <u style={{ width: `${strength}%` }} />
                            </i>
                            {dbm ? `${dbm} dBm` : "—"}
                          </span>
                        </td>
                        <td>{client.rxRate || "—"}</td>
                        <td>{client.txRate || "—"}</td>
                        <td>{client.expectedThroughput || "—"}</td>
                        <td className="r">{client.inactive || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {router.agentEvents && router.agentEvents.length > 0 && (
          <div className="rt-box rt-c12">
            <h3>
              Agent activity
              <span>{router.agentEvents.length}</span>
            </h3>
            <div className="rt-checklist">
              {[...router.agentEvents].reverse().slice(0, 12).map((e, i) => (
                <div key={`${e.at}-${i}`} className="rt-check rt-check--ok">
                  <b>{new Date(e.at).toLocaleTimeString()}</b>
                  <span>{e.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(hasProfile || router.watchdog || (router.logHealth && router.logHealth.issueCount > 0)) && <h4 className="rt-sec">System</h4>}
        {(hasProfile || router.watchdog) && (
          <div className="rt-box rt-c6">
            <h3>{profile?.displayName || t("routerDetails")}</h3>
            <dl className="rt-kv">
              {profile?.location && (
                <>
                  <dt>{t("location")}</dt>
                  <dd>{profile.location}</dd>
                </>
              )}
              {profile?.owner && (
                <>
                  <dt>{t("owner")}</dt>
                  <dd>{profile.owner}</dd>
                </>
              )}
              {profile?.purpose && (
                <>
                  <dt>{t("purpose")}</dt>
                  <dd>{profile.purpose}</dd>
                </>
              )}
              {profile?.notes && (
                <>
                  <dt>{t("notes")}</dt>
                  <dd>{profile.notes}</dd>
                </>
              )}
              {router.watchdog && (
                <>
                  <dt>Watchdog</dt>
                  <dd>
                    <span className={`rt-tag ${router.watchdog.dnsInstalled ? "rt-tag--ok" : "rt-tag--bad"}`}>DNS</span>{" "}
                    <span className={`rt-tag ${router.watchdog.cronRunning ? "rt-tag--ok" : "rt-tag--bad"}`}>cron</span>
                  </dd>
                </>
              )}
            </dl>
          </div>
        )}

        {router.logHealth && router.logHealth.issueCount > 0 && (
          <div className="rt-box rt-c6">
            <h3>
              {t("recentLogWarnings")}
              <span>{router.logHealth.issueCount}</span>
            </h3>
            <div className="rt-warnlog">
              {router.logHealth.recent?.map((line, index) => (
                <div key={index} title={line}>
                  {line}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const RoutersTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const [fixingID, setFixingID] = useState<string | null>(null);
  const [busyActionID, setBusyActionID] = useState<string | null>(null);
  const [checkingRouter, setCheckingRouter] = useState<RouterStatus | null>(null);
  const [editingRouter, setEditingRouter] = useState<RouterStatus | null>(null);
  const [selectedRouterID, setSelectedRouterID] = useState<string | null>(null);

  // Sidebar sub-item clicks open a specific router's details.
  useFocus("routers", (id) => setSelectedRouterID(id));
  useReportSelected("routers", selectedRouterID);

  const { data: sshRouters = [], isLoading, isFetching, error } = useQuery({
    queryKey: ["routers"],
    queryFn: getRouters,
    refetchInterval: 60_000,
  });
  // Routers running the agent report live every few seconds; their numbers replace the SSH ones.
  const { data: agents = [] } = useQuery({ queryKey: ["router-agents"], queryFn: getRouterAgents, refetchInterval: 5_000, retry: false });
  const routers = useMemo(() => sshRouters.map((r) => withAgent(r, agents.find((a) => a.id === r.id))), [sshRouters, agents]);

  const fixMutation = useMutation({
    mutationFn: fixRouterYouTube,
    onMutate: (id) => setFixingID(id),
    onSuccess: (result) => {
      showToast(result.success ? "YouTube fix completed" : "YouTube fix failed", result.success ? "success" : "error", {
        description: result.name,
      });
      queryClient.invalidateQueries({ queryKey: ["routers"] });
    },
    onError: (err: Error) => {
      showToast("YouTube fix failed", "error", { description: err.message });
    },
    onSettled: () => setFixingID(null),
  });

  const runActionMutation = useMutation({
    mutationFn: async (action: { id: string; type: "backup" | "dns" | "watchdog" | "restart"; service?: "zapret" | "dns" | "ssh-tunnel" }) => {
      setBusyActionID(action.id);
      if (action.type === "backup") return backupRouter(action.id);
      if (action.type === "dns") return checkRouterDNS(action.id);
      if (action.type === "watchdog") return installRouterDNSWatchdog(action.id);
      return restartRouterService(action.id, action.service || "zapret");
    },
    onSuccess: (result) => {
      showToast(result.success ? "Router action completed" : "Router action needs attention", result.success ? "success" : "error", {
        description: result.output || result.name,
      });
      queryClient.invalidateQueries({ queryKey: ["routers"] });
    },
    onError: (err: Error) => {
      showToast("Router action failed", "error", { description: err.message });
    },
    onSettled: () => setBusyActionID(null),
  });

  const profileMutation = useMutation({
    mutationFn: ({ id, profile }: { id: string; profile: RouterProfile }) => updateRouterProfile(id, profile),
    onSuccess: (result) => {
      showToast(t("routerProfileSaved"), "success", { description: result.name });
      setEditingRouter(null);
      queryClient.invalidateQueries({ queryKey: ["routers"] });
    },
    onError: (err: Error) => {
      showToast(t("routerProfileSaveFailed"), "error", { description: err.message });
    },
  });

  // The list carries the cheap fields only; the detail view fetches the full
  // snapshot (CPU, interfaces, Wi-Fi radios and clients, log warnings).
  const { data: fullRouter } = useQuery({
    queryKey: ["router", selectedRouterID],
    queryFn: () => getRouter(selectedRouterID as string),
    enabled: Boolean(selectedRouterID),
    refetchInterval: 30_000,
  });
  const selectedRouter = selectedRouterID
    ? fullRouter && fullRouter.id === selectedRouterID
      ? withAgent(fullRouter, agents.find((a) => a.id === fullRouter.id))
      : routers.find((router) => router.id === selectedRouterID) || null
    : null;

  // Mobile: surface Refresh in the top bar (inline button is hidden < sm).
  useMobileHeaderAction(
    () => (
      <button
        onClick={() => queryClient.invalidateQueries({ queryKey: ["routers"] })}
        aria-label={t("refresh")}
        className="flex h-8 w-8 items-center justify-center text-gray-600 dark:text-gray-400"
      >
        <ArrowPathIcon className={cn("h-5 w-5", isFetching && "animate-spin")} />
      </button>
    ),
    [isFetching]
  );

  const [filter, setFilter] = useState<"all" | "problems" | "offline">("all");
  const [query, setQuery] = useState("");

  const actionsFor = (router: RouterStatus): RouterActions => ({
    fixing: fixingID === router.id,
    busyAction: busyActionID === router.id,
    onFix: (id) => fixMutation.mutate(id),
    onCheck: setCheckingRouter,
    onBackup: (id) => runActionMutation.mutate({ id, type: "backup" }),
    onCheckDNS: (id) => runActionMutation.mutate({ id, type: "dns" }),
    onInstallDNSWatchdog: (id) => runActionMutation.mutate({ id, type: "watchdog" }),
    onRestartService: (id, service) => runActionMutation.mutate({ id, type: "restart", service }),
    onEditProfile: setEditingRouter,
  });

  const summary = useMemo(() => {
    const online = routers.filter((r) => r.online).length;
    const problems = routers.filter((r) => routerState(r) === "warn").length;
    const offline = routers.length - online;
    const pings = routers
      .map((r) => r.latency?.find((l) => l.name === "internet"))
      .filter((l): l is NonNullable<typeof l> => !!l && l.ok && !!l.timeMs)
      .map((l) => parseFloat(l.timeMs as string))
      .sort((a, b) => a - b);
    const pingMs = pings.length ? Math.round(pings[Math.floor(pings.length / 2)]) : undefined;
    const ram = routers.map((r) => r.memoryStats?.usedPercent).filter((v): v is number => typeof v === "number");
    const worstRam = ram.length ? Math.max(...ram) : undefined;
    const worst = routers.find((r) => r.memoryStats?.usedPercent === worstRam);
    return { online, problems, offline, pingMs, worstRam, worst };
  }, [routers]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rank = { down: 0, warn: 1, up: 2 } as const;
    return routers
      .filter((r) => {
        const s = routerState(r);
        if (filter === "problems" && s === "up") return false;
        if (filter === "offline" && s !== "down") return false;
        return !q || `${r.name} ${r.sshHost} ${r.model ?? ""} ${r.openwrt ?? ""}`.toLowerCase().includes(q);
      })
      .sort((a, b) => rank[routerState(a)] - rank[routerState(b)] || a.name.localeCompare(b.name));
  }, [routers, filter, query]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["routers"] });
    queryClient.invalidateQueries({ queryKey: ["router"] });
  };

  const modals = (
    <>
      <RouterCheckModal
        router={checkingRouter}
        open={Boolean(checkingRouter)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setCheckingRouter(null);
        }}
      />
      <RouterProfileModal
        router={editingRouter}
        open={Boolean(editingRouter)}
        saving={profileMutation.isPending}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setEditingRouter(null);
        }}
        onSave={(id, profile) => profileMutation.mutate({ id, profile })}
      />
    </>
  );

  if (selectedRouter) {
    return (
      <>
        <RouterDetail
          router={selectedRouter}
          onBack={() => setSelectedRouterID(null)}
          isFetching={isFetching}
          onRefresh={refresh}
          {...actionsFor(selectedRouter)}
        />
        {modals}
      </>
    );
  }

  return (
    <div className="gh sv rt">
      <PageHeader
        title={t("routers")}
        subtitle={`${routers.length} ${routers.length === 1 ? "router" : "routers"}${summary.offline ? ` · ${summary.offline} offline` : ""}`}
        actions={
          <Button variant="secondary" size="sm" onClick={refresh} isLoading={isFetching}>
            <ArrowPathIcon className="h-4 w-4" />
            <span>{t("refresh")}</span>
          </Button>
        }
      />

      {isLoading ? (
        <SkeletonRows rows={6} />
      ) : error ? (
        <ErrorState message={(error as Error).message} onRetry={refresh} />
      ) : routers.length === 0 ? (
        <EmptyState icon={<SignalIcon className="h-8 w-8" />} title={t("noRouters")} hint="Routers are added in the router-bot section of the config." />
      ) : (
        <>
          <div className="rt-kpis">
            <div className={cn("rt-kpi", summary.offline ? "rt-kpi--crit" : "rt-kpi--ok")}>
              <span>Online</span>
              <b>
                {summary.online}
                <small> / {routers.length}</small>
              </b>
              <i>{summary.offline ? `${summary.offline} not responding` : "all reachable"}</i>
            </div>
            <div className={cn("rt-kpi", summary.problems ? "rt-kpi--warn" : "rt-kpi--ok")}>
              <span>Need attention</span>
              <b>{summary.problems}</b>
              <i>{summary.problems ? "failed checks" : "all checks pass"}</i>
            </div>
            <div className="rt-kpi">
              <span>Median ping</span>
              <b>{summary.pingMs !== undefined ? `${summary.pingMs}` : "—"}<small> ms</small></b>
              <i>to the internet</i>
            </div>
            <div className={cn("rt-kpi", summary.worstRam !== undefined && summary.worstRam >= ROUTER_RAM_WARNING_PERCENT && "rt-kpi--warn")}>
              <span>Highest RAM use</span>
              <b>{summary.worstRam !== undefined ? `${Math.round(summary.worstRam)}%` : "—"}</b>
              <i>{summary.worst?.name ?? ""}</i>
            </div>
          </div>

          <div className="rt-bar">
            {(
              [
                ["all", "All", routers.length],
                ["problems", "Problems", summary.problems + summary.offline],
                ["offline", "Offline", summary.offline],
              ] as const
            ).map(([id, label, count]) => (
              <button key={id} type="button" className="rt-chip" aria-pressed={filter === id} onClick={() => setFilter(id)}>
                {label} <em>{count}</em>
              </button>
            ))}
            <label className="rt-search">
              <MagnifyingGlassIcon />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a router…" />
            </label>
          </div>

          {visible.length === 0 ? (
            <div className="sv-note">No routers match.</div>
          ) : (
            <div className="sv-table rt-table">
              <div className="rt-trow rt-thead">
                <span>Router</span>
                <span>Model</span>
                <span>{t("uptime")}</span>
                <span className="rt-cell--num">Load</span>
                <span>RAM</span>
                <span>{t("overlay")}</span>
                <span className="rt-cell--num">Ping</span>
                <span>Checks</span>
                <span />
              </div>
              {[
                { id: "attn", label: "Need attention", items: visible.filter((r) => routerState(r) !== "up") },
                { id: "ok", label: "Healthy", items: visible.filter((r) => routerState(r) === "up") },
              ]
                .filter((g) => g.items.length > 0)
                .map((g) => (
                  <React.Fragment key={g.id}>
                    <div className={`rt-group rt-group--${g.id}`}>
                      <span>{g.label}</span>
                      <em>{g.items.length}</em>
                    </div>
                    {g.items.map((router) => (
                      <RouterRow key={router.id} router={router} onOpen={() => setSelectedRouterID(router.id)} {...actionsFor(router)} />
                    ))}
                  </React.Fragment>
                ))}
            </div>
          )}
        </>
      )}
      {modals}
    </div>
  );
};
